import { currentTabCompanySession, installTabCompanySession } from "./tabCompanySession";
import { closeTenantScopeForDocument, TenantScopeUnavailableError } from "./tenantScopeHeaders";
import { safeLocalStorage } from "../utils/safeLocalStorage";
import { sessionIdentityFromJwt } from "../platform/tenantIdentity";
import { clientDeclarationHeaders } from "../api/clientDeclaration";
/**
 * EmbedAuthContext: Store and manage JWT tokens for iframe embed authentication.
 *
 * This context provides Bearer token authentication as an alternative to cookies,
 * which don't work in cross-origin iframe contexts.
 *
 * Flow:
 * 1. Popup authenticates and mints new session via /embed/mint
 * 2. Popup sends tokens to iframe via postMessage
 * 3. Iframe stores tokens in this context (memory + localStorage)
 * 4. axios interceptor reads from context and adds Authorization header
 * 5. Backend validates JWT and authorizes requests
 *
 * Tokens are stored in:
 * - React Context (memory) - primary source for axios interceptor
 * - localStorage - browser iframe sessions only, to survive iframe recreation
 *
 * Native iOS never receives or stores the refreshable Stytch session token;
 * the SDK keeps it in Keychain and this layer only mirrors five-minute JWTs.
 */

import {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
    useMemo,
    ReactNode,
    useRef,
} from "react";
import {
    invalidateNativeAuthBootstrapSession,
    setEmbedAuthEnabled,
    setEmbedTokenProvider,
    setEmbedRefreshHandler,
    setNativeAuthBootstrapHandler,
    waitForNativeAuthBootstrap,
} from "../config/axios";
import { isInIframe } from "../utils/browserUtils";
import { performLogout } from "../utils/logout";
import StytchAuth, {
    isNativeAuthAvailable,
    nativeTokensFrom,
    restoreNativeProjectRoute,
} from "../native/nativeAuth";
import { resolveApiEnvironment } from "../native/apiEnvironment";
import type { AuthTransport } from "../platform/Platform";
import { getPlatform } from "../platform/platformRef";
import {
    AuthRefreshRejected,
    isDefinitiveAuthRejection,
    NATIVE_AUTH_ERROR_CODE,
} from "../shared/authRejectionClassifier";

const STORAGE_KEY_JWT = "primepoint_embed_session_jwt";
const STORAGE_KEY_TOKEN = "primepoint_embed_session_token";
const STORAGE_KEY_EXP = "primepoint_embed_session_exp";
const REFRESH_THRESHOLD_SECONDS = 60;
const BROADCAST_CHANNEL_NAME = "primepoint-embed-auth";

/** The auth transport of a host whose shell owns the session end to end: it restores,
 *  refreshes, and revokes the session and reports its changes (desktop's Rust auth).
 *  `onSessionChanged` is the member only such a host publishes; iOS's plugin restores
 *  and refreshes too but reports through `nativeAuth.ts`, and the web has none. */
type HostSessionAuth = AuthTransport &
    Required<Pick<AuthTransport, "getSession" | "refresh" | "logout" | "onSessionChanged">>;

function isHostSessionAuth(auth: AuthTransport): auth is HostSessionAuth {
    return (
        auth.onSessionChanged !== undefined &&
        auth.getSession !== undefined &&
        auth.refresh !== undefined &&
        auth.logout !== undefined
    );
}

function hostSessionAuth(): HostSessionAuth | null {
    const { auth } = getPlatform();
    return isHostSessionAuth(auth) ? auth : null;
}

function isNativeSessionAuthAvailable(): boolean {
    return hostSessionAuth() !== null || isNativeAuthAvailable();
}

// Backoff for a refresh that keeps failing for a non-definitive reason (network
// error, timeout, or a status/code that isn't a definitive auth rejection -- see
// isDefinitiveAuthRejection). Scheduled/proactive refresh paths wait out the
// backoff between attempts. A streak never signs out on its own: an iPad on a
// job site can be offline for days, and only the provider's rejection proves
// the session is gone.
const REFRESH_BACKOFF_BASE_MS = 2_000;
const REFRESH_BACKOFF_MAX_MS = 60_000;

function computeRefreshBackoffMs(consecutiveFailures: number): number {
    if (consecutiveFailures <= 0) return 0;
    return Math.min(
        REFRESH_BACKOFF_BASE_MS * 2 ** (consecutiveFailures - 1),
        REFRESH_BACKOFF_MAX_MS,
    );
}

function loadTokensFromStorage(): EmbedAuthTokens | null {
    if (typeof window === "undefined") {
        return null;
    }

    // Native sessions rehydrate from the SDK's Keychain storage on the login
    // route. Purge credentials mirrored by older builds instead of exposing
    // even a short-lived JWT to the WebView's persistent storage.
    if (isNativeSessionAuthAvailable()) {
        safeLocalStorage.removeItem(STORAGE_KEY_JWT);
        safeLocalStorage.removeItem(STORAGE_KEY_TOKEN);
        safeLocalStorage.removeItem(STORAGE_KEY_EXP);
        return null;
    }

    const jwt = safeLocalStorage.getItem(STORAGE_KEY_JWT);
    const token = safeLocalStorage.getItem(STORAGE_KEY_TOKEN);
    const expStr = safeLocalStorage.getItem(STORAGE_KEY_EXP);

    if (!jwt || !token || !expStr) {
        return null;
    }

    const exp = Number.parseInt(expStr, 10);
    if (Number.isNaN(exp)) {
        return null;
    }

    return { sessionJwt: jwt, sessionToken: token, exp };
}

export interface EmbedAuthTokens {
    sessionJwt: string;
    /** Present only for browser iframe sessions; native keeps it in Keychain. */
    sessionToken?: string;
    exp: number; // Unix timestamp
}

interface EmbedAuthContextType {
    tokens: EmbedAuthTokens | null;
    setTokens: (tokens: EmbedAuthTokens | null) => void;
    isAuthenticated: boolean;
    /** Whether this page knows the session it started with. A page that keeps its tokens in
     *  storage knows at once; a native host's page starts with none (the session lives in
     *  the Keychain or the shell) and knows once that read settles (`waitForNativeAuthBootstrap`).
     *  Until then "no tokens" means "not read yet", not "signed out". */
    sessionRead: boolean;
    isExpired: boolean;
    clearTokens: () => void;
    refreshTokens: () => Promise<void>;
}

const EmbedAuthContext = createContext<EmbedAuthContextType | undefined>(undefined);

interface EmbedAuthProviderProps {
    children: ReactNode;
}

export function EmbedAuthProvider({ children }: EmbedAuthProviderProps) {
    // Embed auth is active when inside an iframe OR when valid embed tokens exist in storage
    // (covers "open in new tab" from an embed context)
    const isEmbedded =
        isNativeSessionAuthAvailable() || isInIframe() || loadTokensFromStorage() !== null;
    // Child effects run first. Publish native mode before a child can start an API request.
    setEmbedAuthEnabled(isEmbedded);
    const [tokens, setTokensState] = useState<EmbedAuthTokens | null>(loadTokensFromStorage);
    const refreshTimeoutRef = useRef<number | null>(null);
    const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
    const refreshPromiseRef = useRef<Promise<void> | null>(null);
    const credentialGeneration = useRef(0);
    const consecutiveFailureCountRef = useRef(0);
    const lastFailureAtRef = useRef<number | null>(null);
    const activeTokens = isEmbedded ? tokens : null;
    const activeTokensRef = useRef<EmbedAuthTokens | null>(activeTokens);
    activeTokensRef.current = activeTokens;

    // Define all callbacks before effects that use them
    const updateTokens = useCallback((newTokens: EmbedAuthTokens | null) => {
        // Keep the Axios-facing providers current immediately; React's state
        // update may not render before a launch-time request receives a 401.
        activeTokensRef.current = newTokens;
        setTokensState(newTokens);

        if (newTokens) {
            if (isNativeSessionAuthAvailable()) {
                safeLocalStorage.removeItem(STORAGE_KEY_JWT);
                safeLocalStorage.removeItem(STORAGE_KEY_TOKEN);
                safeLocalStorage.removeItem(STORAGE_KEY_EXP);
            } else {
                safeLocalStorage.setItem(STORAGE_KEY_JWT, newTokens.sessionJwt);
                if (newTokens.sessionToken) {
                    safeLocalStorage.setItem(STORAGE_KEY_TOKEN, newTokens.sessionToken);
                } else {
                    safeLocalStorage.removeItem(STORAGE_KEY_TOKEN);
                }
                safeLocalStorage.setItem(STORAGE_KEY_EXP, String(newTokens.exp));
            }
        } else {
            if (isNativeSessionAuthAvailable()) {
                invalidateNativeAuthBootstrapSession();
            }
            safeLocalStorage.removeItem(STORAGE_KEY_JWT);
            safeLocalStorage.removeItem(STORAGE_KEY_TOKEN);
            safeLocalStorage.removeItem(STORAGE_KEY_EXP);
        }

        // Broadcast token update to other tabs
        if (broadcastChannelRef.current) {
            broadcastChannelRef.current.postMessage({ type: "tokens-updated" });
        }
    }, []);

    const setTokens = useCallback(
        (newTokens: EmbedAuthTokens | null) => {
            // An explicit sign-in/logout is a new credential lineage. Internal renewal
            // below keeps the admitted Document intact until authoritative revalidation.
            credentialGeneration.current++;
            refreshPromiseRef.current = null;
            if (currentTabCompanySession()) {
                closeTenantScopeForDocument();
                installTabCompanySession(null);
            }
            updateTokens(newTokens);
        },
        [updateTokens],
    );

    // Register during render, before any child can mount a query. Axios starts
    // this handler lazily and shares the one promise across every launch-time
    // request plus NativeLogin, avoiding both anonymous requests and duplicate
    // SDK/Keychain restoration calls.
    const bootstrapAuth = hostSessionAuth();
    if (bootstrapAuth) {
        setNativeAuthBootstrapHandler(async () => {
            try {
                const existing = await bootstrapAuth.getSession();
                if (!existing) {
                    return null;
                }
                const restoredTokens = { sessionJwt: existing.sessionJwt, exp: existing.exp };
                setTokens(restoredTokens);
                return restoredTokens;
            } catch {
                // Match the NativeLogin/iOS behavior: failure to restore is not
                // proof that the durable session should be revoked.
                return null;
            }
        });
    } else if (isNativeAuthAvailable()) {
        setNativeAuthBootstrapHandler(async () => {
            try {
                const { environment } = resolveApiEnvironment();
                await StytchAuth.configureEnvironment({ environment });
                const existing = (await StytchAuth.getSession()).session;
                if (!existing) {
                    return null;
                }
                if (existing.resumeProjectPath) restoreNativeProjectRoute(existing);
                const restoredTokens = nativeTokensFrom(existing);
                setTokens(restoredTokens);
                return restoredTokens;
            } catch {
                // Match the previous NativeLogin behavior: failure to restore
                // is not proof that the durable session should be revoked.
                return null;
            }
        });
    }

    const clearTokens = useCallback(() => {
        if (refreshTimeoutRef.current !== null) {
            window.clearTimeout(refreshTimeoutRef.current);
            refreshTimeoutRef.current = null;
        }
        consecutiveFailureCountRef.current = 0;
        lastFailureAtRef.current = null;
        setTokens(null);
    }, [setTokens]);

    const refreshTokens = useCallback(async () => {
        if (!isEmbedded) {
            return;
        }

        const currentTokens = activeTokensRef.current;
        if (!currentTokens) {
            console.log("[EmbedAuth] No tokens available, skipping refresh");
            return;
        }

        // If refresh is already in progress, return existing promise
        if (refreshPromiseRef.current) {
            console.log("[EmbedAuth] Refresh already in progress, waiting for existing refresh...");
            return refreshPromiseRef.current;
        }
        const generation = credentialGeneration.current;
        const assertCurrent = () => {
            if (generation !== credentialGeneration.current)
                throw new TenantScopeUnavailableError("Source session changed during refresh");
        };

        const now = Math.floor(Date.now() / 1000);
        const secondsUntilExpiry = currentTokens.exp - now;
        console.log(`[EmbedAuth] Starting refresh (token expires in ${secondsUntilExpiry}s)`);

        // Create new refresh promise
        const refreshPromise = (async () => {
            try {
                const auth = hostSessionAuth();
                if (auth) {
                    const tauriSession = await auth.refresh();
                    assertCurrent();
                    if (!tauriSession) {
                        // Rust resolved without erroring but returned no session -- as
                        // definitive as an explicit auth rejection, so this carries the
                        // same structured code isDefinitiveAuthRejection checks for.
                        throw Object.assign(new Error("Tauri auth refresh failed: no session"), {
                            code: NATIVE_AUTH_ERROR_CODE.definitive,
                        });
                    }

                    consecutiveFailureCountRef.current = 0;
                    lastFailureAtRef.current = null;
                    // Internal renewal, not a new credential lineage: use the plain
                    // setter so the admitted canonical Document survives the refresh
                    // (setTokens retires it -- see its comment).
                    updateTokens({ sessionJwt: tauriSession.sessionJwt, exp: tauriSession.exp });
                    return;
                }

                if (isNativeAuthAvailable()) {
                    const nativeSession = (await StytchAuth.refresh()).session;
                    assertCurrent();
                    if (!nativeSession) {
                        // The plugin resolved without rejecting but returned no
                        // session -- as definitive as an explicit auth rejection,
                        // so this carries the same structured code
                        // isDefinitiveAuthRejection checks for.
                        throw Object.assign(new Error("Native Stytch refresh failed: no session"), {
                            code: NATIVE_AUTH_ERROR_CODE.definitive,
                        });
                    }

                    consecutiveFailureCountRef.current = 0;
                    lastFailureAtRef.current = null;
                    updateTokens(nativeTokensFrom(nativeSession));
                    return;
                }

                if (!currentTokens.sessionToken) {
                    // Nothing can ever renew this session: as definitive as a rejection.
                    throw Object.assign(
                        new Error("Embed session cannot refresh without a session token"),
                        { code: NATIVE_AUTH_ERROR_CODE.definitive },
                    );
                }
                const response = await fetch("/api/ai/embed/refresh", {
                    method: "POST",
                    headers: {
                        ...clientDeclarationHeaders(getPlatform()),
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ session_token: currentTokens.sessionToken }),
                });

                if (!response.ok) {
                    throw new AuthRefreshRejected(response.status);
                }

                const data: { session_jwt: string; session_token?: string; exp: number } =
                    await response.json();
                assertCurrent();

                console.log(
                    `[EmbedAuth] Refresh successful, new token expires at ${new Date(data.exp * 1000).toISOString()}`,
                );

                consecutiveFailureCountRef.current = 0;
                lastFailureAtRef.current = null;

                updateTokens({
                    sessionJwt: data.session_jwt,
                    sessionToken: data.session_token ?? currentTokens.sessionToken,
                    exp: data.exp,
                });
            } catch (error) {
                // An old renewal must neither resurrect the signed-out account nor
                // clear a replacement account after its own delayed failure.
                assertCurrent();
                console.error("[EmbedAuth] Failed to refresh session", error);

                consecutiveFailureCountRef.current += 1;
                lastFailureAtRef.current = Date.now();

                // Only an explicit auth rejection proves the session is gone. A
                // network error, 429 or 5xx keeps the tokens, however long it
                // lasts: an embed panel stays signed in and retries on its next
                // request, and a native field app stays usable from its durable
                // caches through airplane mode, a cold start or a backend outage.
                if (isDefinitiveAuthRejection(error)) {
                    // Same silent, user-invisible forced recovery as
                    // config/axios.ts's recoverFromEmbedAuthFailure: revoke the
                    // durable native session so a stale/rejected session can't
                    // silently resume on the next refresh or relaunch. Not an
                    // explicit Sign Out, so keep the pinned offline library and
                    // SyncDatabase rows (wipeOfflineData: false).
                    const auth = hostSessionAuth();
                    if (auth) {
                        await auth.logout({ wipeOfflineData: false }).catch(() => undefined);
                    } else if (isNativeAuthAvailable()) {
                        await StytchAuth.logout({ wipeOfflineData: false }).catch(() => undefined);
                    }
                    await performLogout().catch(() => {});
                    clearTokens();
                    if (isNativeSessionAuthAvailable()) {
                        // Native reset invalidates the ConversationEngine lease.
                        // Its page UUID/handshake is intentionally one-shot, so
                        // auth loss must create a fresh JS realm after durable
                        // caches finish clearing rather than SPA-navigating with
                        // the retired lease still installed.
                        const shell = hostSessionAuth();
                        if (shell?.resetAfterSignOut) {
                            // A host of several pages (the desktop) puts every window back to
                            // one signed-out page, its project pages closed.
                            await shell.resetAfterSignOut().catch(() => window.location.reload());
                        } else if (shell) {
                            // The desktop webview is its own single-page app
                            // (desktop/viewer/src/main.tsx), not the client SPA this
                            // provider also hosts inside a document tab -- it serves
                            // no "/authenticate" asset, and every Tauri window opens
                            // at a literal index.html (optionally with a query
                            // string), never a path. Reload the current window
                            // instead: main.tsx's boot sequence re-runs from the
                            // top, which is the same "fresh JS realm" the SPA
                            // navigation below gives on iOS, without navigating to a
                            // path the Tauri asset server can't resolve.
                            window.location.reload();
                        } else {
                            window.location.replace("/authenticate");
                        }
                    }
                }
                throw error;
            } finally {
                // Clear the promise ref so future refreshes can proceed
                if (generation === credentialGeneration.current) refreshPromiseRef.current = null;
            }
        })();

        refreshPromiseRef.current = refreshPromise;
        return refreshPromise;
    }, [isEmbedded, updateTokens, clearTokens]);

    if (isNativeSessionAuthAvailable()) {
        // Child effects run before provider effects. Registering the current
        // native refresh closure here keeps a launch-time 401 on the normal
        // reactive-refresh path instead of treating it as handler-less.
        setEmbedRefreshHandler(refreshTokens);
    }

    // Remaining time before a scheduled/proactive refresh path is allowed to
    // call refreshTokens() again after a recent failure streak. Explicit
    // callers (axios's reactive 401 handling, a user-triggered retry) are
    // NOT gated by this -- they always attempt, per refreshTokens() itself.
    const getRefreshBackoffRemainingMs = useCallback(() => {
        if (consecutiveFailureCountRef.current <= 0 || lastFailureAtRef.current === null) {
            return 0;
        }
        const backoffMs = computeRefreshBackoffMs(consecutiveFailureCountRef.current);
        return Math.max(backoffMs - (Date.now() - lastFailureAtRef.current), 0);
    }, []);

    // Clear the promise ref and failure streak when dependencies change (new
    // tokens from a fresh login, org switch, or successful refresh) to avoid
    // stale promises or carrying a backoff streak into a new session.
    useEffect(() => {
        refreshPromiseRef.current = null;
        consecutiveFailureCountRef.current = 0;
        lastFailureAtRef.current = null;
    }, [activeTokens, setTokens, clearTokens]);

    useEffect(() => {
        setEmbedAuthEnabled(isEmbedded);
        return () => {
            setEmbedAuthEnabled(false);
        };
    }, [isEmbedded]);

    const [sessionRead, setSessionRead] = useState(() => !isNativeSessionAuthAvailable());
    useEffect(() => {
        if (!isNativeSessionAuthAvailable()) {
            return;
        }
        let mounted = true;
        const settle = () => {
            if (mounted) setSessionRead(true);
        };
        void waitForNativeAuthBootstrap().then(settle, settle);
        return () => {
            mounted = false;
            setNativeAuthBootstrapHandler(null);
        };
    }, []);

    // The one-shot bootstrap above resolves once, at mount -- typically with no session,
    // since the desktop shell is signed out at cold start far more often than not. A
    // `login()` that completes afterward (OS-browser flow) has no other way to reach this
    // provider: `nativeAuthBootstrapPromise` (config/axios.ts) caches that first bootstrap
    // result forever, and neither the proactive-refresh nor the visibility-change refresh
    // effect below runs without tokens already present. `onAuthSessionChanged` is the
    // signal `auth.rs` fires once a login/logout actually completes (from this window or
    // another) -- react to it directly instead of re-awaiting the stale bootstrap promise.
    useEffect(() => {
        const auth = hostSessionAuth();
        if (!auth) {
            return;
        }
        let cancelled = false;
        let unsubscribe: (() => void) | null = null;
        void auth
            .onSessionChanged((state) => {
                if (cancelled) return;
                if (!state.authenticated) {
                    clearTokens();
                    return;
                }
                void auth
                    .getSession()
                    .then((session) => {
                        if (cancelled || !session) return;
                        setTokens({ sessionJwt: session.sessionJwt, exp: session.exp });
                    })
                    .catch(() => {
                        // Same forgiving stance as the bootstrap handler above: a failed
                        // re-read is not proof the durable session is gone.
                    });
            })
            .then((stop) => {
                if (cancelled) {
                    stop();
                } else {
                    unsubscribe = stop;
                }
            });
        return () => {
            cancelled = true;
            unsubscribe?.();
        };
    }, [setTokens, clearTokens]);

    const syncStoredTokens = useCallback(() => {
        const next = loadTokensFromStorage();
        const previous = activeTokensRef.current;
        if (!next && !previous) return;
        const oldIdentity = sessionIdentityFromJwt(previous?.sessionJwt);
        const newIdentity = sessionIdentityFromJwt(next?.sessionJwt);
        const sameSubject =
            !!next &&
            !!previous &&
            (next.sessionJwt === previous.sessionJwt ||
                (!!oldIdentity &&
                    !!newIdentity &&
                    oldIdentity.organizationId === newIdentity.organizationId &&
                    oldIdentity.memberId === newIdentity.memberId));
        if (!sameSubject) {
            credentialGeneration.current++;
            refreshPromiseRef.current = null;
            closeTenantScopeForDocument();
            installTabCompanySession(null);
            // Global/legacy pages also hold member and organization caches.
            // Retire every account-bound Document, keeping the new storage for
            // fresh admission instead of installing it beneath the old caches.
            activeTokensRef.current = null;
            setTokensState(null);
            window.location.reload();
            return;
        }
        // Accept a renewal without broadcasting it back or rewriting shared storage.
        activeTokensRef.current = next;
        setTokensState(next);
    }, []);

    // A host whose shell holds the session (desktop, iOS) keeps no token in browser storage
    // (`loadTokensFromStorage`): another page's "tokens-updated" would read as a sign-out here
    // and reload this page, whose own sign-in then reloads that one. The shell tells every page
    // of a sign-in or sign-out itself (`onSessionChanged` below), so these pages skip the sync.
    const shellHoldsSession = isNativeSessionAuthAvailable();

    // Set up BroadcastChannel for cross-tab token sync
    useEffect(() => {
        if (
            !isEmbedded ||
            shellHoldsSession ||
            typeof window === "undefined" ||
            typeof BroadcastChannel === "undefined"
        ) {
            return;
        }

        const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
        broadcastChannelRef.current = channel;

        channel.onmessage = (event: MessageEvent) => {
            if (event.data?.type === "tokens-updated") {
                syncStoredTokens();
            }
        };

        return () => {
            channel.close();
            broadcastChannelRef.current = null;
        };
    }, [isEmbedded, shellHoldsSession, syncStoredTokens]);

    // Fallback: Listen for storage events (for browsers without BroadcastChannel)
    useEffect(() => {
        if (!isEmbedded || shellHoldsSession || typeof window === "undefined") {
            return;
        }

        const handleStorage = (event: StorageEvent) => {
            try {
                if (event.storageArea !== window.localStorage) return;
            } catch {
                return;
            }

            if (
                event.key &&
                ![STORAGE_KEY_JWT, STORAGE_KEY_TOKEN, STORAGE_KEY_EXP].includes(event.key)
            ) {
                return;
            }

            syncStoredTokens();
        };

        window.addEventListener("storage", handleStorage);
        return () => {
            window.removeEventListener("storage", handleStorage);
        };
    }, [isEmbedded, shellHoldsSession, syncStoredTokens]);

    // Memoized token provider to avoid recreating on every render
    const tokenProvider = useCallback(() => {
        if (!isEmbedded) {
            return null;
        }

        const currentTokens = activeTokensRef.current;
        if (!currentTokens || isTokenExpired(currentTokens)) {
            console.log("[EmbedAuth] Token provider called: tokens expired or null");
            return null;
        }
        // No success-path log: the axios interceptor calls this on every
        // request, so logging here floods the console.
        return { sessionJwt: currentTokens.sessionJwt, exp: currentTokens.exp };
    }, [isEmbedded]);

    if (isNativeSessionAuthAvailable()) {
        setEmbedTokenProvider(tokenProvider);
    }

    // Register token provider with axios interceptor
    useEffect(() => {
        if (!isEmbedded) {
            setEmbedTokenProvider(() => null);
            return;
        }

        setEmbedTokenProvider(tokenProvider);
        return () => {
            setEmbedTokenProvider(() => null);
        };
    }, [isEmbedded, tokenProvider]);

    // Register refresh handler with axios interceptor
    useEffect(() => {
        if (!isEmbedded) {
            setEmbedRefreshHandler(async () => {});
            return;
        }

        setEmbedRefreshHandler(refreshTokens);
        return () => {
            setEmbedRefreshHandler(async () => {});
        };
    }, [isEmbedded, refreshTokens]);

    // Handle visibility changes: refresh tokens when tab becomes visible
    useEffect(() => {
        if (!isEmbedded || typeof document === "undefined") {
            return;
        }

        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                console.log("[EmbedAuth] Tab became visible, checking token status");

                if (!activeTokens) {
                    console.log("[EmbedAuth] No tokens, skipping visibility refresh");
                    return;
                }

                const now = Math.floor(Date.now() / 1000);
                const secondsUntilExpiry = activeTokens.exp - now;

                console.log(`[EmbedAuth] Token expires in ${secondsUntilExpiry}s`);

                // If token is expired or expires soon, refresh immediately --
                // unless a recent failure streak's backoff window is still
                // active, in which case skip this proactive attempt.
                if (secondsUntilExpiry <= REFRESH_THRESHOLD_SECONDS) {
                    const backoffRemainingMs = getRefreshBackoffRemainingMs();
                    if (backoffRemainingMs > 0) {
                        console.log(
                            `[EmbedAuth] Refresh backoff active (${Math.ceil(backoffRemainingMs / 1000)}s remaining after ${consecutiveFailureCountRef.current} consecutive failures), skipping proactive visibility refresh`,
                        );
                        return;
                    }
                    console.log(
                        "[EmbedAuth] Token expired or expires soon after tab was hidden, refreshing now",
                    );
                    // Fire-and-forget; errors already logged in refreshTokens()
                    void refreshTokens().catch((err) => {
                        console.error("[EmbedAuth] Error in visibility refresh:", err);
                    });
                }
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [isEmbedded, activeTokens, refreshTokens, getRefreshBackoffRemainingMs]);

    // Scheduled refresh via setTimeout (for active tabs). The delay is the
    // later of (a) the natural pre-expiry threshold and (b) any remaining
    // backoff window from a recent failure streak, so this proactive path
    // never re-attempts a doomed refresh sooner than the backoff allows --
    // the fetch itself, once the timer fires, always attempts.
    useEffect(() => {
        if (refreshTimeoutRef.current !== null) {
            window.clearTimeout(refreshTimeoutRef.current);
            refreshTimeoutRef.current = null;
        }

        if (!isEmbedded || !activeTokens) {
            return;
        }

        const now = Math.floor(Date.now() / 1000);
        const secondsUntilExpiry = activeTokens.exp - now;
        const naturalDelayMs = Math.max((secondsUntilExpiry - REFRESH_THRESHOLD_SECONDS) * 1000, 0);
        const backoffRemainingMs = getRefreshBackoffRemainingMs();
        const refreshDelayMs = Math.max(naturalDelayMs, backoffRemainingMs);

        if (refreshDelayMs <= 0) {
            console.log(
                `[EmbedAuth] Token expires in ${secondsUntilExpiry}s (already within threshold), refreshing immediately via setTimeout`,
            );
            // Fire-and-forget; errors already logged in refreshTokens()
            void refreshTokens().catch((err) => {
                console.error("[EmbedAuth] Error in immediate setTimeout refresh:", err);
            });
            return;
        }

        console.log(
            `[EmbedAuth] Scheduling setTimeout refresh in ${refreshDelayMs / 1000}s` +
                (backoffRemainingMs > naturalDelayMs
                    ? ` (backoff active after ${consecutiveFailureCountRef.current} consecutive failures)`
                    : ` (${REFRESH_THRESHOLD_SECONDS}s before expiry)`),
        );

        refreshTimeoutRef.current = window.setTimeout(() => {
            console.log("[EmbedAuth] setTimeout fired, refreshing token");
            // Fire-and-forget; errors already logged in refreshTokens()
            void refreshTokens().catch((err) => {
                console.error("[EmbedAuth] Error in scheduled setTimeout refresh:", err);
            });
        }, refreshDelayMs);

        return () => {
            if (refreshTimeoutRef.current !== null) {
                window.clearTimeout(refreshTimeoutRef.current);
                refreshTimeoutRef.current = null;
            }
        };
    }, [isEmbedded, activeTokens, refreshTokens, getRefreshBackoffRemainingMs]);

    const isAuthenticated = activeTokens !== null;
    const isExpired = activeTokens !== null && isTokenExpired(activeTokens);

    // Memoized so consumers (NativeNavBridge, useCompatMemberSession, etc.)
    // only re-render when auth state actually changes, not on every render
    // of EmbedAuthProvider.
    const contextValue = useMemo<EmbedAuthContextType>(
        () => ({
            tokens: activeTokens,
            setTokens,
            isAuthenticated,
            sessionRead,
            isExpired,
            clearTokens,
            refreshTokens,
        }),
        [
            activeTokens,
            setTokens,
            isAuthenticated,
            sessionRead,
            isExpired,
            clearTokens,
            refreshTokens,
        ],
    );

    return <EmbedAuthContext.Provider value={contextValue}>{children}</EmbedAuthContext.Provider>;
}

export function useEmbedAuth() {
    const context = useContext(EmbedAuthContext);
    if (context === undefined) {
        throw new Error("useEmbedAuth must be used within EmbedAuthProvider");
    }
    return context;
}

function isTokenExpired(tokens: EmbedAuthTokens): boolean {
    const now = Math.floor(Date.now() / 1000);
    return tokens.exp <= now;
}
